import { MedosClient } from "../../../client/MedosClient";
import { hasVisibleText } from "../../../utils/richText";
export function splitSpecializations(value) {
    return (value ?? "")
        .split(",")
        .map((entry) => entry.trim())
        .filter(Boolean);
}
export function resolveDoctorDisplayMode() {
    if (MedosClient.hasManualDoctorConfig())
        return "manual";
    if (MedosClient.getExternalMemberId())
        return "single";
    return "multi";
}
export function resolveSingleDoctorExternalId() {
    const externalId = MedosClient.getExternalMemberId();
    return typeof externalId === "string" && externalId.length > 0
        ? externalId
        : null;
}
export function collectWorkspaceDoctors(workspace) {
    const seen = new Set();
    const doctors = [];
    const addDoctors = (list) => {
        for (const doctor of list ?? []) {
            if (!seen.has(doctor.id)) {
                seen.add(doctor.id);
                doctors.push(doctor);
            }
        }
    };
    for (const address of workspace.addresses) {
        addDoctors(address.doctors);
    }
    for (const specialization of workspace.specializations ?? []) {
        for (const address of specialization.addresses ?? []) {
            addDoctors(address.doctors);
        }
    }
    return doctors;
}
function normalizeIntroText(intro) {
    if (!isNonEmptyString(intro))
        return null;
    const text = intro
        .replace(/<[^>]+>/gi, " ")
        .replace(/\s+/g, " ")
        .trim();
    return text.length > 0 ? text : null;
}
function normalizeRichText(value) {
    if (!isNonEmptyString(value))
        return null;
    const trimmed = value.trim();
    return hasVisibleText(trimmed) ? trimmed : null;
}
export function fromManual(manual) {
    return {
        source: "manual",
        name: manual.fullName ?? "",
        specializations: manual.specialization ?? [],
        languages: manual.languages ?? [],
        experience: manual.experience ?? null,
        qualifications: (manual.qualifications ?? []).map((q) => ({
            degree: q.degree,
            institution: q.institution,
            year: q.year,
        })),
        profileImageUrl: manual.profileImageUrl ?? null,
        intro: normalizeIntroText(manual.intro ?? null),
        profileSummary: null,
        designation: manual.designation ?? null,
        registrationNumber: manual.registrationNumber ?? null,
        registrationAuthority: manual.registrationAuthority ?? null,
        awards: manual.awards ?? [],
        availableDays: manual.availableDays ?? null,
        services: manual.services ?? [],
        customServices: [],
        education: [],
        expertise: manual.expertise ?? [],
        websiteUrl: null,
        googleRating: null,
        googleReviewUrl: null,
        gender: manual.gender ?? null,
        email: manual.email ?? null,
        phoneNumber: manual.phoneNumber ?? null,
    };
}
function normalizeEducationEntry(value) {
    if (typeof value === "string") {
        return value.trim().length > 0 ? value.trim() : null;
    }
    const degree = typeof value.degree === "string" && value.degree.trim().length > 0
        ? value.degree.trim()
        : null;
    if (!degree)
        return null;
    const institute = typeof value.institute === "string" && value.institute.trim().length > 0
        ? value.institute.trim()
        : null;
    const year = typeof value.year === "string" && value.year.trim().length > 0
        ? value.year.trim()
        : null;
    return [degree, institute, year].filter(Boolean).join(" — ");
}
function normalizeEducationArray(value) {
    if (!Array.isArray(value) || value.length === 0)
        return [];
    return value
        .map(normalizeEducationEntry)
        .filter((item) => typeof item === "string" && item.length > 0);
}
export function fromPublicProfile(profile) {
    const qualifications = [];
    if (profile.qualification) {
        qualifications.push({ degree: profile.qualification });
    }
    const services = Array.from(new Set([...(profile.services ?? []), ...(profile.customServices ?? [])]));
    return {
        source: "publicProfile",
        name: profile.name ?? "",
        prefix: profile.prefix ?? null,
        specializations: splitSpecializations(profile.specialization),
        languages: profile.languages ?? [],
        experience: profile.experience != null ? String(profile.experience) : null,
        qualifications,
        profileImageUrl: resolveProfileImageUrl(profile.profileImageUrl ?? null),
        intro: normalizeRichText(profile.description ?? null),
        profileSummary: normalizeRichText(profile.profileSummary ?? null),
        designation: profile.designation ?? null,
        registrationNumber: profile.registrationNumber ?? null,
        registrationAuthority: profile.registrationAuthority ?? null,
        awards: profile.awards ?? [],
        availableDays: null,
        services,
        customServices: profile.customServices ?? [],
        education: normalizeEducationArray(profile.education),
        expertise: profile.expertise ?? [],
        websiteUrl: profile.websiteUrl ?? null,
        googleRating: profile.googleRating ?? null,
        googleReviewUrl: profile.googleReviewUrl ?? null,
        gender: profile.gender ?? null,
        email: cleanText(profile.email),
        phoneNumber: cleanText(profile.phoneNumber),
    };
}
export function emptyDoctorDisplay() {
    return {
        source: "workspace",
        name: "",
        specializations: [],
        languages: [],
        experience: null,
        qualifications: [],
        profileImageUrl: null,
        intro: null,
        profileSummary: null,
        designation: null,
        registrationNumber: null,
        registrationAuthority: null,
        awards: [],
        availableDays: null,
        services: [],
        customServices: [],
        education: [],
        expertise: [],
        websiteUrl: null,
        googleRating: null,
        googleReviewUrl: null,
        gender: null,
        email: null,
        phoneNumber: null,
    };
}
function isNonEmptyString(value) {
    return typeof value === "string" && value.trim().length > 0;
}
export function cleanText(value) {
    return isNonEmptyString(value) ? value.trim() : null;
}
export function cleanList(items) {
    const cleaned = [];
    for (const item of items) {
        const text = cleanText(item);
        if (text)
            cleaned.push(text);
    }
    return cleaned;
}
const MEDOS_MEDIA_BASE_URL = "https://medos-media.s3.ap-south-1.amazonaws.com/";
export function resolveProfileImageUrl(profileUrl) {
    if (!isNonEmptyString(profileUrl))
        return null;
    if (/^https?:\/\//i.test(profileUrl))
        return profileUrl;
    const path = profileUrl.startsWith("/") ? profileUrl.slice(1) : profileUrl;
    return `${MEDOS_MEDIA_BASE_URL}${path}`;
}
export function isUserDoctorPublicProfile(profile, doctor) {
    return profile.profileType === "USER" && profile.profileId === doctor.id;
}
export function buildDoctorDisplayFromProfile(doctor, profile) {
    if (!profile || !isUserDoctorPublicProfile(profile, doctor)) {
        return emptyDoctorDisplay();
    }
    return fromPublicProfile(profile);
}
export function buildWorkspaceDisplayMap(workspace) {
    const map = {};
    for (const doctor of collectWorkspaceDoctors(workspace)) {
        map[doctor.id] = emptyDoctorDisplay();
    }
    return map;
}
export function buildManualDisplayMap(workspace) {
    const doctors = collectWorkspaceDoctors(workspace);
    const globalManual = MedosClient.getDoctorInfo();
    const singleContext = doctors.length === 1 || Boolean(MedosClient.getExternalMemberId());
    const map = {};
    for (const doctor of doctors) {
        const manual = MedosClient.getDoctorInfoByDoctorId(doctor.id) ??
            (singleContext ? globalManual : null);
        map[doctor.id] = manual ? fromManual(manual) : emptyDoctorDisplay();
    }
    return map;
}
export function getManualOverrideForDoctor(doctorId, workspace) {
    const perDoctor = MedosClient.getDoctorInfoByDoctorId(doctorId);
    if (perDoctor)
        return perDoctor;
    const doctors = collectWorkspaceDoctors(workspace);
    const singleContext = doctors.length === 1 || Boolean(MedosClient.getExternalMemberId());
    return singleContext ? MedosClient.getDoctorInfo() : null;
}
export function hasDoctorContactInfo(display) {
    return Boolean(cleanText(display.email) ??
        cleanText(display.phoneNumber) ??
        cleanText(display.websiteUrl));
}
export function cachePublicProfile(cache, profile) {
    if (profile.profileType !== "USER" || profile.profileId == null)
        return;
    cache.set(profile.profileId, profile);
}
export function findCachedPublicProfileForDoctor(doctor, cache) {
    const byDoctorId = cache.get(doctor.id);
    if (byDoctorId && isUserDoctorPublicProfile(byDoctorId, doctor)) {
        return byDoctorId;
    }
    if (doctor.profileId != null) {
        const byProfileId = cache.get(doctor.profileId);
        if (byProfileId && isUserDoctorPublicProfile(byProfileId, doctor)) {
            return byProfileId;
        }
    }
    for (const profile of cache.values()) {
        if (isUserDoctorPublicProfile(profile, doctor))
            return profile;
    }
    return undefined;
}
export function resolvePublicProfilePathId(doctor) {
    return doctor.profileId ?? doctor.id;
}
export function getDisplayForDoctor(doctor, displayMap) {
    return displayMap[doctor.id] ?? emptyDoctorDisplay();
}
function buildProfileCache(profiles) {
    const cache = new Map();
    for (const profile of profiles)
        cachePublicProfile(cache, profile);
    return cache;
}
export function computeVisibleDoctorIds(workspace, profiles) {
    const cache = buildProfileCache(profiles);
    const ids = [];
    for (const doctor of collectWorkspaceDoctors(workspace)) {
        if (findCachedPublicProfileForDoctor(doctor, cache))
            ids.push(doctor.id);
    }
    return ids;
}
export function buildEnrichedDisplayMap(workspace, profiles, baseMap) {
    const cache = buildProfileCache(profiles);
    const map = { ...baseMap };
    for (const doctor of collectWorkspaceDoctors(workspace)) {
        const cached = findCachedPublicProfileForDoctor(doctor, cache);
        if (cached)
            map[doctor.id] = fromPublicProfile(cached);
    }
    return map;
}
